/**
 * Server-Side Event Allowlist for OneTrust
 *
 * Events in this allowlist will be sent to server-side analytics
 * even when OneTrust is enabled (which normally restricts to client-side only).
 *
 * Use Case: Critical events that must be tracked server-side for data integrity,
 * such as newsletter signups and out-of-stock notifications which require
 * reliable delivery regardless of client-side consent state or page load timing.
 *
 * TODO: Remove after OneTrust CM rollout (if no longer needed)
 */

import { EventType } from './types';

/**
 * Dictionary of events that should always use server-side tracking,
 * mapped to their business justification.
 */
export const SERVER_SIDE_EVENT_ALLOWLIST = {
  // Newsletter signup - Critical for marketing opt-in tracking
  [EventType.EmailSignupCompleted]:
    'Newsletter signup - critical marketing opt-in',
  [EventType.UserEmailCaptured]:
    'Email capture - required for CRM and identify calls',
  [EventType.MarketingOptInFooter]:
    'Marketing consent - legal compliance requirement',

  // Out-of-stock notifications - Critical customer communication
  [EventType.EmailOOSNotificationSignup]:
    'OOS notification - critical customer communication for inventory alerts',
} as const;

/**
 * Check if an event should be allowed to use server-side analytics
 * even when OneTrust is enabled (client-side only mode)
 *
 * @param eventName - The event name to check (e.g., EventType.EmailSignupCompleted)
 * @returns true if the event is on the server-side allowlist
 */
export function isServerSideAllowedEvent(eventName: string): boolean {
  return eventName in SERVER_SIDE_EVENT_ALLOWLIST;
}

/**
 * Get the reason why an event is on the server-side allowlist
 *
 * @param eventName - The event name to look up
 * @returns The business justification string, or undefined if not on allowlist
 */
export function getServerSideAllowlistReason(
  eventName: string,
): string | undefined {
  return SERVER_SIDE_EVENT_ALLOWLIST[
    eventName as keyof typeof SERVER_SIDE_EVENT_ALLOWLIST
  ];
}
